import styles from "./styles.module.scss";
import primaryComponents from "../../../primaryComponents";
import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import { useCustomerForm } from "./useCustomerForm";
import { useValidation } from "./useValidation";
import { MONTHS_OF_YEAR_FULL, DOB_DAYS, DOB_YEARS } from "../../../../helpers";

export const CustomerForm = ({
  customerInfo,
  isEditMode = false,
  closeModal = () => {},
}) => {
  const {
    storeList,
    formData,
    isLoading,
    countriesStatesList,
    stateList,
    setFormData,
    handleCustomer,
  } = useCustomerForm({
    customerInfo,
    isEditMode,
    closeModal,
  });
  const {
    handleSubmit,
    control,
    register,
    trigger,
    formState: { errors },
  } = useValidation({ ...formData });

  return (
    <form
      onSubmit={handleSubmit(handleCustomer)}
      className={styles.formSection}
    >
      <div
        className="d-flex flex-column flex-md-row"
        style={{ columnGap: "10px" }}
      >
        <primaryComponents.InputFieldV2
          control={control}
          register={register}
          name="first_name"
          isRequired={true}
          label="Customer First Name"
          placeholder="Enter customer first name"
          classNames="white"
          value={formData?.first_name}
          onChange={({ target: { value } }) => {
            setFormData((prev) => ({
              ...prev,
              first_name: value,
            }));
          }}
          errorMessage={errors.first_name}
          onKeyUp={() =>
            errors.first_name !== undefined && trigger("first_name")
          }
        />

        <primaryComponents.InputFieldV2
          control={control}
          name="last_name"
          register={register}
          isRequired
          classNames="white"
          label="Customer Last Name"
          placeholder="Enter customer last name"
          value={formData?.last_name}
          onChange={({ target: { value } }) => {
            setFormData((prev) => ({
              ...prev,
              last_name: value,
            }));
          }}
          errorMessage={errors.last_name}
          onKeyUp={() => errors.last_name !== undefined && trigger("last_name")}
        />
      </div>

      <div
        className="d-flex flex-column flex-md-row"
        style={{ columnGap: "10px" }}
      >
        <primaryComponents.PhoneNumberInputFieldV2
          control={control}
          name="phone_number"
          register={register}
          isRequired={true}
          classNames="white"
          label="Customer Phone Number"
          placeholder="Phone Number"
          value={formData?.phone_number}
          onChange={({ target: { value } }) => {
            setFormData((prev) => ({
              ...prev,
              phone_number: value,
            }));
          }}
          errorMessage={errors.phone_number}
          onKeyUp={() =>
            errors.phone_number !== undefined && trigger("phone_number")
          }
          phoneCodeEditable={true}
          onChangePhoneCode={(val) => {
            setFormData((prev) => ({
              ...prev,
              phone_code: val,
            }));
          }}
          countryCode={formData?.phone_code}
        />

        <primaryComponents.InputFieldV2
          control={control}
          name="email"
          register={register}
          classNames="white mr-1"
          label="Customer Email Address"
          placeholder="Enter customer email address"
          value={formData?.email}
          onChange={({ target: { value } }) => {
            setFormData((prev) => ({
              ...prev,
              email: value,
            }));
          }}
          errorMessage={errors.email}
        />
      </div>

      {/* Date of Birth */}
      <div
        className="d-flex flex-column flex-md-row"
        style={{ columnGap: "10px" }}
      >
        <div className="w-100">
          <primaryComponents.SelectFieldV2
            control={control}
            name="dob_day"
            label="Day of Birth"
            isRequired={true}
            placeholder="Day"
            value={
              formData?.dob_day
                ? String(formData.dob_day).padStart(2, "0")
                : undefined
            }
            options={DOB_DAYS}
            idKey="id"
            nameKey="name"
            errorMessage={errors.dob_day}
            onChange={(value) => {
              setFormData((prev) => ({ ...prev, dob_day: value }));
              if (errors.dob_day !== undefined) trigger("dob_day");
            }}
          />
        </div>
        <div className="w-100">
          <primaryComponents.SelectFieldV2
            control={control}
            name="dob_month"
            label="Month of Birth"
            isRequired={true}
            placeholder="Month"
            value={
              formData?.dob_month
                ? MONTHS_OF_YEAR_FULL[formData.dob_month - 1]
                : undefined
            }
            options={MONTHS_OF_YEAR_FULL.map((name, i) => ({
              id: i + 1,
              name,
            }))}
            idKey="id"
            nameKey="name"
            errorMessage={errors.dob_month}
            onChange={(value) => {
              setFormData((prev) => ({ ...prev, dob_month: value }));
              if (errors.dob_month !== undefined) trigger("dob_month");
            }}
          />
        </div>
        <div className="w-100">
          <primaryComponents.SelectField
            label="Year of Birth (optional)"
            placeholder="Year"
            value={formData?.dob_year ? String(formData.dob_year) : undefined}
            options={DOB_YEARS}
            idKey="id"
            nameKey="name"
            onChange={(value) => {
              setFormData((prev) => ({ ...prev, dob_year: value }));
            }}
          />
        </div>
      </div>

      <div
        className="d-flex flex-column flex-md-row"
        style={{ columnGap: "10px" }}
      >
        <div
          className="d-flex align-items-center w-100 mb-3"
          style={{ gap: "20px" }}
        >
          <primaryComponents.RadioButton
            name={"customerGender"}
            id="MALE"
            checked={formData.gender === "MALE"}
            setRadioValue={() =>
              setFormData((prev) => ({ ...prev, gender: "MALE" }))
            }
          >
            Male
          </primaryComponents.RadioButton>
          <primaryComponents.RadioButton
            name={"customerGender"}
            id="FEMALE"
            checked={formData.gender === "FEMALE"}
            setRadioValue={() =>
              setFormData((prev) => ({ ...prev, gender: "FEMALE" }))
            }
          >
            Female
          </primaryComponents.RadioButton>
        </div>
        <div className="w-100">
          <primaryComponents.SelectField
            options={storeList}
            label={"Select store"}
            nameKey="name"
            value={
              formData?.store_id ? customerInfo?.store?.name : "Select stores"
            }
            onChange={(value) => {
              setFormData((prev) => ({
                ...prev,
                store_id: value,
              }));
            }}
            idKey={"id"}
            classNames="store-select"
          />
        </div>

        <primaryComponents.InputField
          classNames="white"
          name="uniqueID"
          label="Customer Unique ID"
          placeholder="Enter Customer Unique ID"
          value={formData?.company_customer_id}
          onChange={({ target: { value } }) => {
            setFormData((prev) => ({
              ...prev,
              company_customer_id: value,
            }));
          }}
        />
      </div>

      <div
        className="d-flex flex-column flex-md-row"
        style={{ columnGap: "10px" }}
      >
        <div className="w-100">
          <primaryComponents.SelectField
            label="Country"
            value={formData?.country ? formData?.country : "Select country"}
            onChange={(value) => {
              setFormData((prev) => ({
                ...prev,
                country: value,
              }));
            }}
            options={countriesStatesList}
            nameKey="name"
            idKey="name"
          />
        </div>

        <div className="w-100">
          <primaryComponents.SelectField
            classNames="white"
            label="State"
            placeholder="Select State"
            value={
              stateList?.find(
                (state) => `${state?.code}` === `${formData?.state}`,
              )?.name || "Select state"
            }
            onChange={(value) => {
              setFormData((prev) => ({
                ...prev,
                state: value,
              }));
            }}
            nameKey="name"
            idKey={"code"}
            options={[...stateList]}
          />
        </div>

        <primaryComponents.InputField
          classNames="white"
          label="City/Town"
          placeholder="Enter City"
          value={formData?.city}
          onChange={({ target: { value } }) => {
            setFormData((prev) => ({
              ...prev,
              city: value,
            }));
          }}
        />
      </div>

      <div className={`${globalStyles.cardWithBorder} p-4`}>
        <div className="d-flex justify-content-between">
          <h6 className="mb-0 small-text-size">
            Enable loyalty for this customer
          </h6>

          <primaryComponents.Switch
            name={"loyalty"}
            id={formData}
            isChecked={formData?.is_loyalty_eligible}
            onChange={(value) => {
              setFormData((prev) => ({
                ...prev,
                is_loyalty_eligible: value,
              }));
            }}
          />
        </div>
      </div>

      <primaryComponents.Button
        classNames={"btn btn--primary cover mt-4"}
        isLoading={isLoading}
        isDisabled={isLoading}
        loadingText={isEditMode ? "Updating..." : "Adding..."}
      >
        {isEditMode ? "Update" : "Add Customer"}
      </primaryComponents.Button>
    </form>
  );
};
